import { Injectable } from '@angular/core';
import { Marked } from 'marked';
import DOMPurify from 'dompurify';

/**
 * Balises autorisées dans le HTML produit par {@link LegalMarkdownService.renderToSafeHtml}, une
 * fois passé par DOMPurify. Couvre le contenu légal standard (titres, paragraphes, listes, liens,
 * mise en forme, citations, code) ainsi que les tableaux GFM (`table`/`thead`/`tbody`/`tfoot`/
 * `tr`/`td`/`th`), fréquents dans les CGU/politiques de confidentialité (grilles de sous-traitants,
 * durées de conservation, etc.). `del`/`s` couvrent le barré GFM (`~~texte~~`).
 */
const ALLOWED_TAGS = [
  'p', 'div', 'ul', 'ol', 'li', 'a',
  'strong', 'em', 'b', 'i', 'del', 's',
  'blockquote', 'code', 'pre', 'br', 'hr',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th'
];

/**
 * Attributs autorisés sur les balises ci-dessus. Volontairement minimal : ni `data-*` ni
 * `contenteditable` — ce service ne rend jamais de contenu destiné à être réédité (contrairement
 * à `MarkdownService`, dédié au rich-text-editor WYSIWYG).
 */
const ALLOWED_ATTR = ['class', 'href', 'target', 'rel'];

/**
 * Passerelle Markdown -> HTML sûr pour l'affichage public des documents légaux (CGU, politique de
 * confidentialité, mentions légales — `LegalDocumentVersion.content`).
 *
 * Délibérément SÉPARÉ de `MarkdownService` (utilisé par `RichTextEditorComponent`) :
 * - `MarkdownService` a un `ALLOWED_TAGS` calibré pour un contenteditable WYSIWYG (checklists
 *   `span`/`data-file-id`, mentions `data-mention-id`, attribut `contenteditable`) — l'élargir
 *   pour couvrir les tables GFM créerait une régression de sécurité sur un composant sensible
 *   sans rapport avec les documents légaux.
 * - `MarkdownService` utilise un renderer marked custom (réécriture des task-lists en checklists,
 *   des liens `file-ref:`/`mention:` en ancres spéciales) qui n'a aucun sens pour du contenu
 *   légal publié : ce service utilise une instance `Marked` neutre, sans renderer custom.
 * - Le contenu vient de la base (table `legal_document_versions`, écriture restreinte au
 *   super-admin Web-atrio côté RLS) : DOMPurify + `[innerHTML]` standard (jamais de
 *   `bypassSecurityTrust*`) offre une défense en profondeur même si l'écriture est déjà contrôlée.
 */
@Injectable({ providedIn: 'root' })
export class LegalMarkdownService {
  private readonly marked = new Marked({ gfm: true, breaks: false });

  /** Rend du Markdown (GFM) en HTML assaini par DOMPurify, prêt pour `[innerHTML]`. */
  renderToSafeHtml(markdown: string): string {
    const rawHtml = this.marked.parse(markdown, { async: false });
    return DOMPurify.sanitize(rawHtml, { ALLOWED_TAGS, ALLOWED_ATTR });
  }
}
